import React, { useMemo, useCallback } from "react";
import type { QuickListOption, QuickKeyConfig } from "@/types";
import { useStyles } from "./styles";

export interface QuickListProps {
  /** 当前触发的快捷键配置 */
  config: QuickKeyConfig;
  /** 当前键盘选中的选项下标 */
  activeIndex?: number;
  /** 选择回调 */
  onSelect: (option: QuickListOption) => void;
  /** 关闭回调 */
  onClose: () => void;
  /** 是否可见 */
  visible: boolean;
}

const QuickList: React.FC<QuickListProps> = ({
  config,
  activeIndex = 0,
  onSelect,
  onClose,
  visible,
}) => {
  const styles = useStyles();

  // SDK 内置列表只负责展示 options；搜索、远程过滤、排序等能力由业务方在外部更新 options 实现。
  const groupedOptions = useMemo(() => {
    const groups: Record<string, QuickListOption[]> = {};
    config.options.forEach((opt) => {
      const g = opt.group || "";
      if (!groups[g]) groups[g] = [];
      groups[g].push(opt);
    });
    return groups;
  }, [config.options]);

  const handleSelect = useCallback(
    (opt: QuickListOption) => {
      // 先通知选择，再关闭弹层；插入 tag 的逻辑由 XAdkSender 统一处理。
      onSelect(opt);
      onClose();
    },
    [onSelect, onClose],
  );

  if (!visible) return null;

  const normalizedActiveIndex =
    config.options.length > 0
      ? ((activeIndex % config.options.length) + config.options.length) %
        config.options.length
      : 0;
  const activeOptionId = config.options[normalizedActiveIndex]?.id;
  const getItemClassName = (opt: QuickListOption) =>
    `${styles.quickListItem}${opt.id === activeOptionId ? " active" : ""}`;

  const renderOption = (opt: QuickListOption) => {
    if (config.renderItem) {
      return (
        <div
          key={opt.id}
          className={getItemClassName(opt)}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => handleSelect(opt)}
        >
          {config.renderItem(opt)}
        </div>
      );
    }
    return (
      <div
        key={opt.id}
        className={getItemClassName(opt)}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => handleSelect(opt)}
      >
        {opt.icon && <span className="quick-list-item-icon">{opt.icon}</span>}
        <div className="quick-list-item-content">
          <div className="quick-list-item-label">{opt.label}</div>
          {opt.description && (
            <div className="quick-list-item-desc">{opt.description}</div>
          )}
        </div>
      </div>
    );
  };

  const groupKeys = Object.keys(groupedOptions);

  return (
    <div className={styles.quickList}>
      {config.title && (
        <div className={styles.quickListHeader}>{config.title}</div>
      )}
      <div className={styles.quickListBody}>
        {groupKeys.length === 0 && (
          <div className={styles.quickListEmpty}>暂无可选项</div>
        )}
        {groupKeys.map((group) => {
          const items = groupedOptions[group];
          return (
            <div key={group}>
              {group && (
                <div className={styles.quickListGroup}>
                  {group.replace(/_/g, " / ")}
                </div>
              )}
              {items.map(renderOption)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuickList;
